import React, { useState } from 'react';

import { CreateUpdateSalesInvoiceOrderNumberDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';
import momencik from '../../../common/momencik';
import { convert } from '../../../common/pokCore/contexts/SalesInvoiceOrderNumberContext';
import {
  SortableList,
  SortableListColumn,
} from '../../../common/components/SortableList/SortableList';

import { SalesInvoiceOrderNumberEditor } from './SalesInvoiceOrderNumberEditor';

interface SalesInvoiceOrderNumbersProps {
  items: CreateUpdateSalesInvoiceOrderNumberDto[];
  readOnly?: boolean;
  onItemsChange?: (items: CreateUpdateSalesInvoiceOrderNumberDto[]) => void;
}

export const SalesInvoiceOrderNumbers: React.FC<
  SalesInvoiceOrderNumbersProps
> = ({ items, readOnly, onItemsChange }) => {
  const { t, tk } = useTranslation('fvsLng');

  const [showEditor, setShowEditor] = useState(false);
  const [editingDto, setEditingDto] =
    useState<CreateUpdateSalesInvoiceOrderNumberDto | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAdd = () => {
    setEditingDto(convert());
    setEditingIndex(null);
    setShowEditor(true);
  };

  const handleEdit = (index: number) => {
    setEditingDto(items[index]);
    setEditingIndex(index);
    setShowEditor(true);
  };

  const handleSave = (dto: CreateUpdateSalesInvoiceOrderNumberDto) => {
    let updated: CreateUpdateSalesInvoiceOrderNumberDto[];
    if (editingIndex !== null) {
      updated = items.map((item, i) => (i === editingIndex ? dto : item));
    } else {
      updated = [...items, dto];
    }
    onItemsChange?.(updated);
    setShowEditor(false);
    setEditingDto(null);
    setEditingIndex(null);
  };

  const handleDelete = (index: number) => {
    onItemsChange?.(items.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    setShowEditor(false);
    setEditingDto(null);
    setEditingIndex(null);
  };

  const handleMove = (index: number, delta: -1 | 1) => {
    const newIndex = index + delta;
    if (newIndex < 0 || newIndex >= items.length) {
      return;
    }
    const reordered = [...items];
    [reordered[index], reordered[newIndex]] = [
      reordered[newIndex],
      reordered[index],
    ];
    onItemsChange?.(reordered);
  };

  const columns: SortableListColumn<CreateUpdateSalesInvoiceOrderNumberDto>[] =
    [
      {
        header: t(tk.finance.lp),
        render: (_, index) => index + 1,
      },
      {
        header: t(tk.finance.orderNumber),
        render: item => item.orderNumber,
      },
      {
        header: t(tk.finance.orderDate),
        render: item =>
          item.orderDate ? momencik(item.orderDate, 'YYYY-MM-DD') : '–',
      },
    ];

  return (
    <>
      <SortableList
        title={t(tk.finance.orderNumbers)}
        addLabel={t(tk.finance.addOrderNumber)}
        items={items}
        columns={columns}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onMoveUp={index => handleMove(index, -1)}
        onMoveDown={index => handleMove(index, 1)}
        readOnly={readOnly}
        bordered={true}
        emptyMessage={t(tk.finance.noOrderNumbers)}
        editTooltip={t(tk.finance.editOrderNumber)}
        deleteTooltip={t(tk.finance.deleteOrderNumber)}
        deleteConfirmation={t(tk.finance.deleteOrderNumberConfirm)}
      />
      {showEditor && editingDto && (
        <SalesInvoiceOrderNumberEditor
          show={showEditor}
          dto={editingDto}
          title={
            editingIndex !== null
              ? t(tk.finance.editOrderNumber)
              : t(tk.finance.addOrderNumber)
          }
          onSave={handleSave}
          onClose={handleClose}
        />
      )}
    </>
  );
};
